<script setup lang="ts">
import { computed } from 'vue';
import { usePage, router } from '@inertiajs/vue3';
import { useI18n } from 'vue-i18n';
import { Bell, Check, ExternalLink, Menu } from 'lucide-vue-next';
import { useSidebar } from '@/components/ui/sidebar/utils';
import { useLocale } from '@/composables/useLocale';
import { show as creatorsShow } from '@/routes/creators';
import { index as notificationsIndex, read } from '@/routes/notifications';
import Breadcrumbs from '@/components/Breadcrumbs.vue';
import CommandPalette from '@/components/CommandPalette.vue';
import UserMenuContent from '@/components/UserMenuContent.vue';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuTrigger,
    DropdownMenuLabel,
    DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import type { BreadcrumbItem } from '@/types';

withDefaults(
    defineProps<{
        breadcrumbs?: BreadcrumbItem[];
    }>(),
    {
        breadcrumbs: () => [],
    },
);

type InAppNotification = {
    id: string;
    data: { title: string; body?: string };
    readAt: string | null;
    createdAt: string;
};

type NotificationData = {
    unreadCount: number;
    latest: InAppNotification[];
} | null;

const { t } = useI18n();
const page = usePage<{
    auth: { user: any };
    creatorNav: { avatarUrl: string | null; slug: string } | null;
    notificationBell: NotificationData;
}>();
const user = computed(() => page.props.auth.user);
const creatorAvatarUrl = computed(() => page.props.creatorNav?.avatarUrl ?? null);
const { locale, urlForLocale } = useLocale();
const creatorPageUrl = computed(() => page.props.creatorNav ? urlForLocale(creatorsShow.url(page.props.creatorNav.slug), locale.value) : null);

const { toggleSidebar } = useSidebar();
const notificationData = computed(() => page.props.notificationBell);
const unreadCount = computed(() => notificationData.value?.unreadCount ?? 0);
const latestNotifications = computed(() => notificationData.value?.latest ?? []);

function getUserInitials(name: string): string {
    return name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
}

function timeAgo(dateString: string): string {
    const now = new Date();
    const date = new Date(dateString);
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 60) return t('backend.topbar.justNow');

    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m`;

    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h`;

    const days = Math.floor(hours / 24);
    return `${days}d`;
}

function markAsRead(e: Event, notification: InAppNotification) {
    e.stopPropagation();
    e.preventDefault();
    if (notification.readAt) return;

    router.patch(read.url(notification.id), {}, {
        preserveScroll: true,
    });
}

function navigateToNotifications() {
    router.visit(notificationsIndex.url());
}
</script>

<template>
    <header
        class="sticky top-0 z-10 flex h-14 shrink-0 items-center gap-3 border-b border-border bg-background px-4 md:gap-4 md:px-6"
    >
        <!-- Mobile sidebar trigger -->
        <button
            class="inline-flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground md:hidden"
            @click="toggleSidebar"
        >
            <Menu class="size-5" :stroke-width="1.6" />
            <span class="sr-only">Menu</span>
        </button>

        <!-- Breadcrumbs -->
        <div class="hidden items-center gap-1.5 text-[13px] text-muted-foreground sm:flex">
            <template v-if="breadcrumbs && breadcrumbs.length > 0">
                <Breadcrumbs :breadcrumbs="breadcrumbs" />
            </template>
        </div>

        <!-- Right actions -->
        <div class="ml-auto flex items-center gap-1.5 md:gap-2.5">
            <!-- View page link -->
            <a
                v-if="creatorPageUrl"
                :href="creatorPageUrl"
                target="_blank"
                class="inline-flex h-[30px] items-center justify-center gap-1.5 rounded-md border border-border bg-card text-[12px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground w-[30px] md:w-auto md:px-2.5"
            >
                <ExternalLink class="size-3.5 shrink-0" :stroke-width="1.6" />
                <span class="hidden md:inline">{{ t('backend.topbar.viewPage') }}</span>
            </a>

            <!-- Command Palette search (hidden on mobile) -->
            <div class="hidden sm:block">
                <CommandPalette />
            </div>

            <!-- Notification bell -->
            <DropdownMenu>
                <DropdownMenuTrigger as-child>
                    <button class="relative inline-flex size-[30px] items-center justify-center rounded-md border border-border bg-card transition-colors hover:bg-muted">
                        <Bell class="size-4 text-nv-ink2" :stroke-width="1.6" />
                        <span v-if="unreadCount > 0" class="absolute right-[5px] top-1 size-1.5 rounded-full bg-nv-indigo" />
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" class="w-[340px] p-0">
                    <DropdownMenuLabel class="px-4 py-3 text-[12px] font-semibold">
                        {{ t('backend.topbar.notifications') }}
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator class="m-0" />

                    <template v-if="latestNotifications.length > 0">
                        <div
                            v-for="n in latestNotifications"
                            :key="n.id"
                            class="group relative flex items-start gap-3 px-4 py-3 transition-colors hover:bg-muted/50"
                        >
                            <!-- Unread dot -->
                            <div class="mt-1.5 shrink-0">
                                <span
                                    class="block size-2 rounded-full"
                                    :class="n.readAt ? 'bg-transparent' : 'bg-nv-indigo'"
                                />
                            </div>

                            <!-- Content -->
                            <div class="flex flex-1 flex-col gap-0.5 pr-7">
                                <span class="text-[13px] font-medium text-foreground">{{ n.data.title }}</span>
                                <span v-if="n.data.body" class="text-[12px] leading-snug text-muted-foreground">{{ n.data.body }}</span>
                                <span class="mt-0.5 text-[11px] text-muted-foreground/70">{{ timeAgo(n.createdAt) }}</span>
                            </div>

                            <!-- Mark as read button on hover -->
                            <button
                                v-if="!n.readAt"
                                class="absolute right-3 top-3 flex size-6 items-center justify-center rounded-md opacity-0 transition-opacity hover:bg-muted group-hover:opacity-100"
                                :title="t('backend.topbar.markRead')"
                                @click="markAsRead($event, n)"
                            >
                                <Check class="size-3.5 text-muted-foreground" :stroke-width="2" />
                            </button>
                        </div>

                        <DropdownMenuSeparator class="m-0" />

                        <button
                            class="flex w-full items-center justify-center py-2.5 text-[12px] font-medium text-nv-indigo transition-colors hover:bg-muted/50"
                            @click="navigateToNotifications"
                        >
                            {{ t('backend.topbar.showMore') }}
                        </button>
                    </template>

                    <div v-else class="px-4 py-8 text-center">
                        <Bell class="mx-auto mb-2 size-6 text-muted-foreground/30" :stroke-width="1.4" />
                        <p class="text-[12px] text-muted-foreground">{{ t('backend.topbar.noNotifications') }}</p>
                    </div>
                </DropdownMenuContent>
            </DropdownMenu>

            <!-- User menu -->
            <DropdownMenu>
                <DropdownMenuTrigger as-child>
                    <button class="inline-flex size-[30px] items-center justify-center overflow-hidden rounded-full bg-muted text-[11px] font-semibold text-foreground transition-colors hover:bg-muted/80">
                        <img v-if="creatorAvatarUrl" :src="creatorAvatarUrl" :alt="user.name" class="size-full object-cover" />
                        <span v-else>{{ getUserInitials(user.name) }}</span>
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" class="w-56">
                    <UserMenuContent :user="user" />
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    </header>
</template>
